<template>
  <div class="inbound-status-card">
    <div class="status-header">
      <h5>Inbound Status</h5>
      <el-button 
        type="text" 
        size="mini" 
        icon="el-icon-refresh" 
        @click="refreshStatus"
        :loading="refreshing">
        Refresh
      </el-button>
    </div>
    
    <div class="status-content" v-if="hasValidData">
      <div class="status-grid">
        <div class="status-item">
          <span class="label">Take Quantity:</span>
          <span class="value primary">{{ takeQuantity }}</span>
        </div>
        
        <div class="status-item">
          <span class="label">Total Remaining:</span>
          <span class="value">{{ totalRemaining }}</span>
        </div>
        
        <!-- 关键信息：待处理认领 -->
        <div class="status-item warning" v-if="safePendingClaims > 0">
          <span class="label">
            <i class="el-icon-warning"></i>
            Pending Claims:
          </span>
          <span class="value">{{ safePendingClaims }}</span>
        </div>
        
        <!-- 关键信息：真实可认领数量 -->
        <div class="status-item success">
          <span class="label">
            <i class="el-icon-check"></i>
            Available:
          </span>
          <span class="value emphasis">{{ availableToClaim }}</span>
        </div>
        
        <div class="status-item" v-if="safePrice !== null">
          <span class="label">Price:</span>
          <span class="value">${{ safePrice.toFixed(2) }}</span>
        </div>
      </div>
      
      <!-- 待处理认领的详细说明 -->
      <div class="pending-note" v-if="safePendingClaims > 0">
        <small class="text-muted">
          <i class="el-icon-info"></i>
          {{ safePendingClaims }} items awaiting warehouse review
        </small>
      </div>
    </div>
    
    <!-- 无数据状态 -->
    <div class="no-data-state" v-else>
      <div class="no-data-content">
        <i class="el-icon-warning"></i>
        <p>No inbound data available</p>
      </div>
    </div>
    
    <!-- 状态提示 -->
    <div class="status-tips" v-if="pendingClaims > 0">
      <el-alert
        title="Notice"
        :description="getStatusTipMessage()"
        type="info"
        :closable="false"
        show-icon>
      </el-alert>
    </div>
    
    <!-- 操作区域 -->
    <div class="status-actions" v-if="showActions && hasValidData">
      <el-button 
        type="primary" 
        size="small" 
        @click="$emit('select-inbound', inbound)"
        :disabled="availableToClaim <= 0">
        Select This Inbound
      </el-button>
    </div>
  </div>
</template>

<script>
import { mixedPackageApi } from '../api/mixedPackage'

export default {
  name: 'InboundStatusCard',
  props: {
    inbound: {
      type: Object,
      required: true
    },
    showActions: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      pendingClaims: 0,
      refreshing: false,
      statusLoaded: false
    }
  },
  computed: {
    safeInbound() {
      return this.inbound || {}
    },
    takeQuantity() {
      const qty = this.safeInbound.qty
      return (qty !== null && qty !== undefined && !isNaN(qty)) ? Number(qty) : 0
    },
    totalRemaining() {
      const leftNum = this.safeInbound.leftNum
      return (leftNum !== null && leftNum !== undefined && !isNaN(leftNum)) ? Number(leftNum) : 0
    },
    safePendingClaims() {
      return (this.pendingClaims !== null && this.pendingClaims !== undefined && !isNaN(this.pendingClaims)) ? Number(this.pendingClaims) : 0
    },
    availableToClaim() {
      return Math.max(0, this.totalRemaining - this.safePendingClaims)
    },
    safePrice() {
      const price = this.safeInbound.price
      return (price !== null && price !== undefined && !isNaN(price) && price > 0) ? Number(price) : null
    },
    hasValidData() {
      return this.safeInbound && (this.safeInbound.inboundNo || this.safeInbound.qty !== undefined)
    }
  },
  mounted() {
    this.loadPendingClaims()
  },
  watch: {
    'inbound.inboundNo': {
      handler() {
        this.loadPendingClaims()
      },
      immediate: true
    }
  },
  methods: {
    async loadPendingClaims() {
      if (!this.inbound || !this.inbound.inboundNo) return
      
      try {
        const result = await mixedPackageApi.getPendingClaimQuantity(this.inbound.inboundNo)
        if (result.code === '200' || result.code === 200) {
          this.pendingClaims = result.data || 0
          this.statusLoaded = true
        } else {
          console.warn('获取待处理认领数量失败:', result.message)
          this.pendingClaims = 0
        }
      } catch (error) {
        console.error('获取Inbound状态失败:', error)
        this.pendingClaims = 0
      }
    },
    
    async refreshStatus() {
      this.refreshing = true
      try {
        await this.loadPendingClaims()
        this.$emit('status-refreshed', {
          inboundNo: this.inbound.inboundNo,
          pendingClaims: this.pendingClaims,
          available: Math.max(0, this.inbound.leftNum - this.pendingClaims)
        })
      } finally {
        this.refreshing = false
      }
    },
    
    getStatusTipMessage() {
      return `You have ${this.safePendingClaims} items awaiting warehouse review. Only ${this.availableToClaim} items are available for new claims.`
    }
  }
}
</script>

<style scoped>
.inbound-status-card {
  border: 1px solid #dcdfe6;
  border-radius: 6px;
  padding: 16px;
  background: #fafafa;
  margin-bottom: 16px;
}

.status-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
  border-bottom: 1px solid #ebeef5;
  padding-bottom: 8px;
}

.status-header h5 {
  margin: 0;
  color: #303133;
  font-size: 16px;
  font-weight: 600;
}

.status-content {
  margin-bottom: 12px;
}

/* 流式网格布局 */
.status-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 8px;
}

.status-item {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  min-width: 120px;
  max-width: 200px;
  flex: 1 1 calc(25% - 9px); /* 4列布局，减去gap */
  padding: 8px 12px;
  background: #f8f9fa;
  border-radius: 4px;
  border-left: 3px solid #e4e7ed;
}

.status-item .label {
  color: #606266;
  font-weight: 500;
  font-size: 12px;
  margin-bottom: 4px;
  display: flex;
  align-items: center;
  white-space: nowrap;
}

.status-item .label i {
  margin-right: 4px;
  font-size: 14px;
}

.status-item .value {
  color: #303133;
  font-weight: 600;
  font-size: 16px;
  line-height: 1.2;
}

.status-item .value.primary {
  color: #409eff;
  font-size: 18px;
}

.status-item .value.emphasis {
  color: #67c23a;
  font-size: 18px;
  font-weight: bold;
}

/* 状态主题色 */
.status-item.warning {
  border-left-color: #e6a23c;
  background: #fdf6ec;
}

.status-item.warning .label {
  color: #e6a23c;
}

.status-item.warning .value {
  color: #e6a23c;
}

.status-item.success {
  border-left-color: #67c23a;
  background: #f0f9ff;
}

.status-item.success .label {
  color: #67c23a;
}

/* 待处理认领说明 */
.pending-note {
  margin-top: 8px;
  padding: 6px 12px;
  background: #fdf6ec;
  border-radius: 4px;
  border: 1px solid #f5dab1;
}

.pending-note .text-muted {
  color: #e6a23c;
  font-size: 12px;
}

.pending-note i {
  margin-right: 4px;
}

/* 无数据状态样式 */
.no-data-state {
  padding: 20px;
  text-align: center;
}

.no-data-content {
  color: #909399;
}

.no-data-content i {
  font-size: 24px;
  margin-bottom: 8px;
  display: block;
}

.no-data-content p {
  margin: 0;
  font-size: 14px;
}

.status-tips {
  margin-bottom: 12px;
}

.status-actions {
  text-align: center;
  padding-top: 8px;
  border-top: 1px solid #ebeef5;
}

/* 响应式设计 */
@media (max-width: 768px) {
  .status-item {
    flex: 1 1 calc(50% - 6px); /* 小屏幕时2列布局 */
    min-width: 140px;
  }
}

@media (max-width: 480px) {
  .status-item {
    flex: 1 1 100%; /* 超小屏幕时单列布局 */
    min-width: 100%;
  }
  
  .status-grid {
    gap: 8px;
  }
}
</style>


// WEBPACK FOOTER //
// InboundStatusCard.vue?3a7dfb8d